'use client';

import { c as _c } from "react/compiler-runtime";
import { MaskitoOptions } from '@maskito/core';
import { useCallback, useMemo, useState } from 'react';
import { COUNTRY_CODE_US, CountryCode } from '@sola/schema/dotcom/shared/geo.ts';

// (___) ___-____
export const USPhoneNumberMask = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

// 27703
export const postalCodeMask = new Array(5).fill(/\d/);
export const postalCodeMaskUS = [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
export const postalCodeMaskCanada = [/[a-zA-Z]/, /\d/, /[a-zA-Z]/, ' ', /\d/, /[a-zA-Z]/, /\d/];
const dateMasks = {
  enAndEs: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  ko: [/\d/, /\d/, /\d/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/]
};
export type GeneratePhoneOrPostalMaskFunc = (countryCode: CountryCode) => {
  options?: MaskitoOptions;
  placeholder?: string;
};

/**
 * Hook for generating masks, placeholders, and validations based on country ISO code.
 */
export const usePhoneMask = countryCode => {
  const $ = _c(6);
  const [generateMask, setGenerateMask] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      const generateMaskFunc = _temp;
      setGenerateMask(() => generateMaskFunc);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const loadMask = t0;
  let t1;
  bb0: {
    if (!generateMask) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[1] !== countryCode || $[2] !== generateMask) {
      t2 = generateMask(countryCode);
      $[1] = countryCode;
      $[2] = generateMask;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  const mask = t1;
  let t2;
  if ($[4] !== mask) {
    t2 = {
      loadMask,
      mask
    };
    $[4] = mask;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const usePostalMask = countryCode => {
  const $ = _c(6);
  const [generateMask, setGenerateMask] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      const generateMaskFunc = _temp2;
      setGenerateMask(() => generateMaskFunc);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const loadMask = t0;
  let t1;
  bb0: {
    if (!generateMask) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[1] !== countryCode || $[2] !== generateMask) {
      t2 = generateMask(countryCode);
      $[1] = countryCode;
      $[2] = generateMask;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  const mask = t1;
  let t2;
  if ($[4] !== mask) {
    t2 = {
      loadMask,
      mask
    };
    $[4] = mask;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
type GenerateDateMaskFunc = (localeCode: string) => {
  options?: MaskitoOptions;
  placeholder?: string;
};
export const useDateMask = localeCode => {
  const $ = _c(6);
  const [generateMask, setGenerateMask] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      const generateMaskFunc = _temp3;
      setGenerateMask(() => generateMaskFunc);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const loadMask = t0;
  let t1;
  bb0: {
    if (!generateMask) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[1] !== generateMask || $[2] !== localeCode) {
      t2 = generateMask(localeCode);
      $[1] = generateMask;
      $[2] = localeCode;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  const mask = t1;
  let t2;
  if ($[4] !== mask) {
    t2 = {
      loadMask,
      mask
    };
    $[4] = mask;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

// 1234 1234 1234 1234 123
export const CCNumberMask = [...new Array(4).fill(/\d/), ' ', ...new Array(4).fill(/\d/), ' ', ...new Array(4).fill(/\d/), ' ', ...new Array(4).fill(/\d/), ' ', ...new Array(3).fill(/\d/)];

// 123
export const CCVMask = [...new Array(3).fill(/\d/)];

// 12/34
export const CCDateMask = [...new Array(2).fill(/\d/), '/', ...new Array(2).fill(/\d/)];

// 01637017
export const sponsorDonorMask = new Array(8).fill(/\d/);
function _temp(countryCodeToMask) {
  return countryCodeToMask === COUNTRY_CODE_US ? {
    options: {
      mask: USPhoneNumberMask
    },
    placeholder: "(___) ___-____"
  } : {
    options: {
      mask: /^[0-9]*$/
    }
  };
}
function _temp2(countryCodeToMask) {
  switch (countryCodeToMask) {
    case "US":
      {
        return {
          options: {
            mask: postalCodeMaskUS
          }
        };
      }
    case "MX":
      {
        return {
          options: {
            mask: postalCodeMask
          }
        };
      }
    case "CA":
      {
        return {
          options: {
            mask: postalCodeMaskCanada
          }
        };
      }
    default:
      {
        return {};
      }
  }
}
function _temp3(localeCodeToMask) {
  switch (localeCodeToMask) {
    case "ko":
      {
        return {
          options: {
            mask: dateMasks.ko
          },
          placeholder: "yyyy.mm.dd"
        };
      }
    case "es":
      {
        return {
          options: {
            mask: dateMasks.enAndEs
          },
          placeholder: "dd/mm/yyyy"
        };
      }
    default:
      {
        return {
          options: {
            mask: dateMasks.enAndEs
          },
          placeholder: "mm/dd/yyyy"
        };
      }
  }
}